import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { HotelSearchForm, Result as HotelSearchResult } from '~/components/Common/Forms/HotelSearchForm';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import OffersService from '~/services/OffersService';
import {
  TourFlashAccommodationRoom,
  deleteTourFlashAccommodation,
  getTourFlashAccommodation,
  saveTourFlashAccommodation,
} from '~/services/ToursService';

const MAX_ROOMS = 3;
/** Matches the cap svc-tour enforces. */
const MAX_NIGHTS = 30;

interface Props {
  tourId: string;
}

interface SelectedOffer {
  id: string;
  name: string;
  propertyId?: string;
}

interface OfferRoomOption {
  packageId: string;
  lePackageId: string;
  roomRateId: string;
  name: string;
  detail?: string;
}

interface RoomRow {
  key: string;
  lePackageId: string;
}

let rowSequence = 0;
const newRoomRow = (): RoomRow => ({
  key: `room-${(rowSequence += 1)}`,
  lePackageId: '',
});

export default function TourFlashAccommodation({ tourId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();

  const [isFetching, setIsFetching] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingRooms, setIsLoadingRooms] = useState(false);
  const [hasSavedConfiguration, setHasSavedConfiguration] = useState(false);
  const [isChangingHotel, setIsChangingHotel] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<SelectedOffer | undefined>(undefined);
  const [offerRoomOptions, setOfferRoomOptions] = useState<Array<OfferRoomOption>>([]);
  const [numberOfNights, setNumberOfNights] = useState<string>('1');
  const [roomRows, setRoomRows] = useState<Array<RoomRow>>([newRoomRow()]);

  const loadOfferRooms = useCallback(
    async (offerId: string) => {
      setIsLoadingRooms(true);
      try {
        const response = await OffersService.getOffer(offerId);
        const packages: Array<App.AccommodationPackage> = response?.result?.packages ?? [];
        setOfferRoomOptions(
          packages.map((offerPackage) => {
            const roomTypeName = offerPackage.room_type?.name;
            return {
              // Mirrors how svc-public-offer's calendar builds a package id, so
              // the two cannot drift.
              packageId: offerPackage.id_salesforce_external ?? offerPackage.le_package_id,
              // svc-calendar keys availability on this pair.
              lePackageId: offerPackage.le_package_id,
              roomRateId: offerPackage.fk_room_rate_id,
              name: roomTypeName ?? offerPackage.name,
              detail: roomTypeName && roomTypeName !== offerPackage.name ? offerPackage.name : undefined,
            };
          }),
        );
      } catch (error) {
        enqueueSnackbar(`Could not load rooms for offer ${offerId}. ${error?.message ?? ''}`, { variant: 'error' });
        setOfferRoomOptions([]);
      } finally {
        setIsLoadingRooms(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    const fetchExistingConfiguration = async () => {
      setIsFetching(true);
      try {
        const { result } = await getTourFlashAccommodation(tourId);
        if (result) {
          setHasSavedConfiguration(true);
          setSelectedOffer({
            id: result.offerId,
            name: result.offerName ?? result.offerId,
            propertyId: result.propertyId,
          });
          setNumberOfNights(String(result.numberOfNights));
          setRoomRows(
            result.rooms.map((room) => ({
              ...newRoomRow(),
              lePackageId: room.lePackageId,
            })),
          );
          await loadOfferRooms(result.offerId);
        }
      } catch (error) {
        enqueueSnackbar(`Could not load the Flash accommodation configuration. ${error?.message ?? ''}`, {
          variant: 'error',
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchExistingConfiguration();
  }, [tourId, loadOfferRooms, enqueueSnackbar]);

  const handleOfferSelect = async (selection: HotelSearchResult) => {
    // The typeahead returns the property in `id` and its offer in `fk`.
    if (!selection.fk) {
      enqueueSnackbar('That property has no Flash offer attached.', { variant: 'warning' });
      return;
    }

    const offerId = selection.fk;
    setSelectedOffer({ id: offerId, name: selection.name, propertyId: selection.id });
    setRoomRows([newRoomRow()]);
    setIsChangingHotel(false);
    await loadOfferRooms(offerId);
  };

  const handleRoomChange = (key: string, lePackageId: string) => {
    setRoomRows((rows) => rows.map((row) => (row.key === key ? { ...row, lePackageId } : row)));
  };

  const handleAddRoom = () => {
    setRoomRows((rows) => (rows.length >= MAX_ROOMS ? rows : [...rows, newRoomRow()]));
  };

  const handleRemoveRoom = (key: string) => {
    setRoomRows((rows) => rows.filter((row) => row.key !== key));
  };

  const chosenPackageIds = useMemo(() => roomRows.map((row) => row.lePackageId).filter(Boolean), [roomRows]);

  const nightsError = useMemo(() => {
    const nights = Number(numberOfNights);
    if (!Number.isInteger(nights) || nights < 1) {
      return 'Must be a whole number of nights';
    }
    // The input's max attribute is only a hint, so the cap is enforced here.
    return nights > MAX_NIGHTS ? `Cannot be more than ${MAX_NIGHTS} nights` : undefined;
  }, [numberOfNights]);

  const validationError = useMemo(() => {
    if (!selectedOffer) {
      return 'Choose a Flash hotel first';
    }
    if (nightsError) {
      return 'Check the number of nights';
    }
    if (roomRows.some((row) => !row.lePackageId)) {
      return 'Every row needs a room selected';
    }
    if (new Set(chosenPackageIds).size !== chosenPackageIds.length) {
      return 'The same room is offered twice';
    }
    return undefined;
  }, [selectedOffer, nightsError, roomRows, chosenPackageIds]);

  const handleSave = async () => {
    if (validationError || !selectedOffer) {
      return;
    }

    setIsSaving(true);
    try {
      const rooms: Array<TourFlashAccommodationRoom> = roomRows.map((row) => {
        const roomOption = offerRoomOptions.find((option) => option.lePackageId === row.lePackageId);
        return {
          packageId: roomOption?.packageId,
          lePackageId: row.lePackageId,
          roomRateId: roomOption?.roomRateId ?? '',
          name: roomOption?.name ?? row.lePackageId,
        };
      });

      await saveTourFlashAccommodation(tourId, {
        offerId: selectedOffer.id,
        offerName: selectedOffer.name,
        propertyId: selectedOffer.propertyId,
        numberOfNights: Number(numberOfNights),
        rooms,
      });
      setHasSavedConfiguration(true);
      enqueueSnackbar('Flash accommodation saved.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Could not save Flash accommodation. ${error?.message ?? ''}`, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveConfiguration = async () => {
    const confirmed = await showConfirmDialog({
      title: 'Remove Flash accommodation?',
      description: 'The hotel will stop being offered with this tour. You can set it up again at any time.',
    });
    if (!confirmed) {
      return;
    }

    setIsSaving(true);
    try {
      await deleteTourFlashAccommodation(tourId);
      setHasSavedConfiguration(false);
      setSelectedOffer(undefined);
      setOfferRoomOptions([]);
      setNumberOfNights('1');
      setRoomRows([newRoomRow()]);
      enqueueSnackbar('Flash accommodation removed.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Could not remove Flash accommodation. ${error?.message ?? ''}`, { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isFetching) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  const showSearch = !selectedOffer || isChangingHotel;

  return (
    <Stack spacing={3} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={2}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            The Flash hotel bundled into this tour, and the rooms guests choose between when they book.
          </Typography>
        </Box>
      </Stack>

      <Paper variant="outlined">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          divider={<Divider orientation="vertical" flexItem />}
          alignItems={{ md: 'center' }}
        >
          <Box p={2} flexGrow={1} minWidth={0}>
            <Typography variant="overline" color="text.secondary">
              Hotel
            </Typography>
            {selectedOffer && !isChangingHotel && (
              <Stack direction="row" spacing={1.5} alignItems="center" mt={0.5}>
                <HotelOutlinedIcon color="action" />
                <Box minWidth={0}>
                  <Typography fontWeight={600} noWrap>
                    {selectedOffer.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Offer {selectedOffer.id}
                  </Typography>
                </Box>
                <Button size="small" variant="text" onClick={() => setIsChangingHotel(true)}>
                  Change
                </Button>
              </Stack>
            )}
            {showSearch && (
              <Box mt={1} maxWidth={480}>
                <HotelSearchForm
                  label="Search by location or property name"
                  type="le"
                  id="tour-flash-accommodation"
                  onSelect={handleOfferSelect}
                />
                {isChangingHotel && (
                  <Button size="small" variant="text" onClick={() => setIsChangingHotel(false)} sx={{ mt: 1 }}>
                    Cancel
                  </Button>
                )}
              </Box>
            )}
          </Box>

          <Box p={2}>
            <Typography variant="overline" color="text.secondary">
              Stay length
            </Typography>
            <Box mt={0.5}>
              <TextField
                type="number"
                size="small"
                value={numberOfNights}
                onChange={(event) => setNumberOfNights(event.target.value)}
                error={!!nightsError}
                helperText={nightsError}
                slotProps={{
                  htmlInput: { min: 1, max: MAX_NIGHTS, 'aria-label': 'Number of nights' },
                  input: { endAdornment: <InputAdornment position="end">nights</InputAdornment> },
                }}
                sx={{ width: 160 }}
              />
            </Box>
          </Box>
        </Stack>
      </Paper>

      <Box>
        <Stack direction="row" justifyContent="right" mb={1}>
          <Typography variant="body2" color="text.secondary">
            {roomRows.length} of {MAX_ROOMS}
          </Typography>
        </Stack>

        {!selectedOffer && <Alert severity="info">Choose a Flash hotel above to pick its rooms.</Alert>}

        {selectedOffer && isLoadingRooms && (
          <Box p={3} display="flex" justifyContent="center">
            <CircularProgress size={28} />
          </Box>
        )}

        {selectedOffer && !isLoadingRooms && offerRoomOptions.length === 0 && (
          <Alert severity="warning">This offer has no packages, so there are no rooms to choose from.</Alert>
        )}

        {selectedOffer && !isLoadingRooms && offerRoomOptions.length > 0 && (
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={200}>Tier</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell width={56} />
                </TableRow>
              </TableHead>
              <TableBody>
                {roomRows.map((row, index) => {
                  const isIncludedRoom = index === 0;
                  const isDuplicate =
                    !!row.lePackageId && chosenPackageIds.filter((id) => id === row.lePackageId).length > 1;

                  return (
                    <TableRow key={row.key}>
                      <TableCell>
                        {isIncludedRoom ? (
                          <Chip size="small" color="success" label="Included in tour price" />
                        ) : (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" variant="outlined" label={`Upgrade ${index}`} />
                            <Typography variant="caption" color="text.secondary">
                              pays the difference
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <TextField
                            select
                            size="small"
                            value={row.lePackageId}
                            onChange={(event) => handleRoomChange(row.key, String(event.target.value))}
                            error={isDuplicate}
                            helperText={isDuplicate ? 'Already offered' : undefined}
                            sx={{ minWidth: 300 }}
                            slotProps={{
                              htmlInput: { 'aria-label': `Room ${index + 1}` },
                            }}
                          >
                            {offerRoomOptions.map((option) => (
                              <MenuItem key={option.lePackageId} value={option.lePackageId}>
                                {option.name}
                                {option.detail && (
                                  <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                                    {option.detail}
                                  </Typography>
                                )}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        {!isIncludedRoom && (
                          <Tooltip title="Remove room">
                            <IconButton size="small" aria-label="Remove room" onClick={() => handleRemoveRoom(row.key)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Box p={1.5}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddRoom}
                disabled={roomRows.length >= MAX_ROOMS || offerRoomOptions.length <= roomRows.length}
              >
                Add room
              </Button>
            </Box>
          </Paper>
        )}

        {selectedOffer && offerRoomOptions.length > 0 && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            Offer up to three rooms. The guest pays the difference between the cheapest of them and the one they choose,
            priced live from the hotel offer on the day.
          </Typography>
        )}
      </Box>

      <Stack direction="row" spacing={1}>
        {hasSavedConfiguration && (
          <Button variant="text" color="error" onClick={handleRemoveConfiguration} disabled={isSaving}>
            Remove
          </Button>
        )}
        <Tooltip title={validationError ?? ''}>
          <span>
            <Button variant="contained" onClick={handleSave} disabled={!!validationError || isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
